import { Pencil, X } from "lucide-react";
import { useEffect, useState } from "react";

const createForm = {
    DeviceId: "",
    DeviceCode: "",
    DeviceName: "",
    DeviceType: "",
    SerialNumber: "",
    Location: "CDL",
    Status: "A"
};

function EquipmentModal({
    open,
    onClose,
    mode,
    equipment,
    equipmentList,
    sourceDevices,
    onSubmit,
    onUpdate
}) {
    
    const [modalMode, setModalMode] = useState(mode);
    const [form, setForm] = useState(createForm);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);

    const configuredCodes = new Set(
        (equipmentList || []).map(x => x.DeviceCode)
    );

    const availableDevices = (sourceDevices || []).filter(
        x => !configuredCodes.has(x.DeviceCode)
    )

    const availableUniqueDevices = Array.from(
        new Map(
            availableDevices.map(
                item => [item.DeviceCode, item]
            )
        ).values()
    )

    useEffect(() => {
        if (equipment /* && modalMode === "view" */) {
            setForm({
                DeviceId: equipment.DeviceId || "",
                DeviceCode: equipment.DeviceCode || "",
                DeviceName: equipment.DeviceName || "",
                DeviceType: equipment.DeviceType !== "" ? equipment.DeviceType : "N/A",
                SerialNumber: equipment.SerialNumber || "",
                Location: equipment.Location || "CDL",
                Status: equipment.Status || "A"
            })
        }

        if (modalMode === "create") {
            setForm(createForm)
        }
    }, [equipment, modalMode, open]);

    useEffect(() => {
        setModalMode(mode);
    }, [mode]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm(prev => ({
            ...prev,
            [name]: value
        }));

        if (submitted) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }))
        }
    }

    const handleDeviceSelection = (e) => {
        const deviceCode = e.target.value

        const selected = availableUniqueDevices.find(item => item.DeviceCode === deviceCode)

        if (!selected) return;

        // Extract the numeric part after _ or - in the device code using a regex
        const match = selected.DeviceCode.match(/[_-](\d+)$/);

        // setForm({
        //     ...form,
        //     DeviceCode: selected.DeviceCode,
        //     DeviceId: match ? parseInt(match[1], 10) : "",
        //     DeviceType: selected.DeviceType === "G" ? "Generator" : selected.DeviceType === "P" ? "Power Panel" : ""
        // })

        setForm(prev => ({
            ...prev,
            DeviceCode: selected.DeviceCode,
            DeviceId: match ? parseInt(match[1], 10) : "",
            DeviceType: selected.DeviceType === "G" ? "Generator" : selected.DeviceType === "P" ? "Power Panel" : ""
        }));

        if (submitted) {
            setErrors(prev => ({
                ...prev,
                DeviceCode: undefined
            }))
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!form.DeviceName.trim()) {
            newErrors.DeviceName = "Device name is required";
        }

        if (!form.SerialNumber.trim()) {
            newErrors.SerialNumber = "Serial number is required";
        } else if (!/^\d+$/.test(form.SerialNumber.trim())) {
            newErrors.SerialNumber = "Serial number must be a number";
        }

        if (!form.DeviceCode) {
            newErrors.DeviceCode = "Please select a device";
        }

        return newErrors;
    }

    const handleEdit = () => {
        setModalMode("edit");
    }

    const handleCreate = () => {
        setSubmitted(true);

        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        onSubmit?.(form)
    }

    const handleUpdate = () => {
        setSubmitted(true);

        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        onUpdate?.(form)
    }

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white w-full max-w-[850px] rounded-3xl max-h-[550px] md:max-h-[90vh] overflow-y-auto border border-border shadow-2xl">
                
                <div className="px-4 md:px-8 py-4 md:py-6 border-b border-border">
                    {/* <p className="text-xs uppercase tracking-[3px] text-electricBlue">
                        Asset Registry
                    </p> */}

                    <div className="flex justify-between items-center mt-2">
                        <h2 className="font-bold text-2xl">
                            {
                                modalMode === "create"
                                    ? "Register Equipment"
                                    : modalMode === "edit"
                                    ? "Edit Equipment"
                                    : "Equipment Details"
                            }
                        </h2>
                        
                        <div className="flex items-center gap-3">
                            {
                                modalMode === "view" && (
                                    <Pencil 
                                        size={18}
                                        className="cursor-pointer text-electricBlue"
                                        onClick={handleEdit}
                                    />
                                )
                            }

                            <button 
                                onClick={onClose}
                                className="p-2 rounded-lg hover:bg-slate-100 transition"
                                aria-label="Close modal"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="p-3 md:p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                        {
                            modalMode === "create" && (
                                <Select 
                                    label="Device"
                                    value={form.DeviceCode}
                                    onChange={handleDeviceSelection}
                                    error={submitted ? errors.DeviceCode : undefined}
                                    options={[
                                        {
                                            value: "",
                                            label: "Select Device"
                                        },
                                        ...availableUniqueDevices.map((device) => ({
                                            value: device.DeviceCode,
                                            label: device.DeviceCode,
                                        }))
                                    ]}
                                />
                            )
                        }

                        <Input 
                            label="Device ID"
                            name="DeviceId"
                            value={form.DeviceId}
                            disabled
                        />

                        <Input 
                            label="Device Code"
                            name="DeviceCode"
                            value={form.DeviceCode}
                            disabled
                        />

                        <Input 
                            label="Device Name"
                            name="DeviceName"
                            value={form.DeviceName}
                            onChange={handleChange}
                            error={submitted ? errors.DeviceName : undefined}
                            disabled={modalMode === "view"}
                        />

                        <Input 
                            label="Serial Number"
                            name="SerialNumber"
                            value={form.SerialNumber}
                            onChange={handleChange}
                            error={submitted ? errors.SerialNumber : undefined}
                            disabled={modalMode === "view"}
                        />

                        <Input 
                            label="Location"
                            name="Location"
                            value={form.Location}
                            onChange={handleChange}
                            disabled={modalMode === "view"}
                        />

                        <Input 
                            label="Device Type"
                            name="DeviceType"
                            value={form.DeviceType}
                            disabled
                        />

                        <Select 
                            label="Status"
                            name="Status"
                            value={form.Status}
                            onChange={handleChange}
                            disabled={modalMode === "view"}
                            options={[
                                {
                                    value: "A",
                                    label: "Active"
                                },
                                {
                                    value: "I",
                                    label: "Inactive"
                                }
                            ]}
                        />
                    </div>
                </div>

                <div className="flex flex-col-reverse md:flex-row justify-end gap-3 px-4 md:px-8 py-4 md:py-6 border-t border-border">
                    <button
                        onClick={onClose}
                        className="w-full md:w-auto px-5 py-3 rounded-xl border border-border hover:bg-slate-50"
                    >
                        Cancel
                    </button>

                    {
                        modalMode === "create" && (
                            <button
                                onClick={handleCreate}
                                className="w-full md:w-auto px-5 py-3 rounded-xl bg-deepNavy text-white hover:bg-navy transition"
                            >
                                Create
                            </button>
                        )
                    }

                    {
                        modalMode === "edit" && (
                            <button
                                onClick={handleUpdate}
                                className="w-full md:w-auto px-5 py-3 rounded-xl bg-deepNavy text-white hover:bg-navy transition"
                            >
                                Update
                            </button>
                        )
                    }
                </div>
            </div>
        </div>
    )

}

const Input = ({
    label,
    error,
    ...props
}) => (
    <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-textSecondary">
            {label}
        </label>

        <input 
            {...props}
            className={`w-full px-4 py-1.5 md:py-3 rounded-xl border bg-white focus:ring-4 outline-none
            ${
                error ? "border-red-500 focus:ring-red-100" : "border-border focus:border-electricBlue focus:ring-electricBlue/10 "
            }
            disabled:bg-slate-100 disabled:text-slate-500`}
        />

        {error && (
            <span className="text-xs text-red-500">
                {error}
            </span>
        )}
    </div>
)

const Select = ({
    label,
    error,
    options,
    ...props
}) => {
    return (
        <div className="flex flex-col gap-1">
            <label className="text-sm">
                {label}
            </label>

            <select
                {...props}
                className={`w-full px-4 py-1.5 md:py-3 rounded-xl border bg-white focus:ring-4 outline-none 
                ${
                    error ? "border-red-500 focus:ring-red-100" : "border-border focus:border-electricBlue focus:ring-electricBlue/10 "
                }
                disabled:bg-slate-100 disabled:text-slate-500`}
            >
                {
                    options.map(
                        option => (
                            <option
                                key={option.value}
                                value={option.value}
                            >
                                {option.label}
                            </option>
                        )
                    )
                }
            </select>
        </div>
    )
}

export default EquipmentModal;